/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import Cardinality from './Cardinality';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/utils/Cardinality (static) // Cardinality

/**
 * JoinGraph is a utility class used to traverse the join graph of a module. Requires JSONObject from joingraph REST endpoint. { "connector": [ { "leftID" : "QS V5 expr ID" "rightID": "QS V5 expr ID" "cardinality": "1:1 || N:1 || 1:N" } ], "queryitem": [ { "QS V5 ID": [ "QI V5 ID" ] } ], "group": [ "Visible QS V5 expr ID" ] } Retrieves related query items from given query subjects.
 * @author  Alan.Tran@ibm.com
 */
export default class JoinGraph extends BaseMoserObject{

	static MAX_DISTANCE = 2147483647;

	static DUMMY_START_GRP_ID = "theDummyGroupID";

	static CONNECTOR_OFFSET = 1000000;

	groupsMap = null;

	queryItemsMap = null;

	allConnectors = null;

	unwantedCardinality = null;



	constructor(joinGraph) {
		super();
		this.groupsMap = new JoinGraph.GroupSet();
		this.allConnectors = [];
		if (!joinGraph) {
			return;
		}
		this.queryItemsMap = {};
		if (joinGraph.hasOwnProperty("connector") && joinGraph.hasOwnProperty("queryitem") && joinGraph.hasOwnProperty("group")) {
			var joinGraphConnectors = joinGraph["connector"];
			var joinGraphQueryItems = joinGraph["queryitem"];
			var joinGraphGroups = joinGraph["group"];
			this._populateGroups(joinGraphGroups);
			this._populateConnectors(joinGraphConnectors);
			this._populateQueryItems(joinGraphQueryItems);
		}
	}

	/**
	 * @param {Array} inputIDs                      List of queryitem ID, calculation ID, or group ID
	 * @return {Array}   		                    Returns list of related queryitem IDs
	 * @throws {Error} IllegalArgumentException 		ID is not found or accessible; Graph is disconnected
	 */
	getRelated(inputIDs) {
		return this._getRelatedQueryItems(inputIDs, null);
	}

	/**
	 * @param {Array} inputIDs                      List of queryitem ID, calculation ID, or group ID
	 * @return {Array}   		                    Returns list of related queryitem IDs that are fanned out by Nto1
	 * @throws {Error} IllegalArgumentException 		ID is not found or accessible; Graph is disconnected
	 */
	getRelatedNto1(inputIDs) {
		return this._getRelatedQueryItems(inputIDs, Cardinality.MANY_TO_ONE);
	}

	/**
	 * @param {Array} inputIDs                      List of queryitem ID, calculation ID, or group ID
	 * @param {Cardinality} cardinality 					Get related based on cardinality
	 * @return {Array}   		                    Returns list of related queryitem IDs
	 * @throws {Error} IllegalArgumentException 		ID is not found or accessible; Graph is disconnected
	 */
	_getRelatedQueryItems(inputIDs, cardinality) {
		var inputGroups = new JoinGraph.GroupSet();
		if (!inputIDs || inputIDs.length==0) {
			return [];
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = inputIDs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var id = __exp_enFor0[__i_enFor0];
			if (this.queryItemsMap.hasOwnProperty(id)) {
				for (var __i_enFor1 = 0, __exp_enFor1 = this.queryItemsMap[id]._getGroups(), __len_enFor1 = __exp_enFor1.length;
						__i_enFor1 < __len_enFor1; ++__i_enFor1) {
					var group = __exp_enFor1[__i_enFor1];
					inputGroups._add(group);
				}
			} else if (this.groupsMap._containsGroupID(id)) {
				inputGroups._add(this.groupsMap._getByGroupID(id));
			} else {
				throw new Error("ID is not found or accessible: " + id);
			}
		}
		if (inputGroups._isEmpty()) {
			return [];
		}
		if (!(this._isConnected(inputGroups))) {
			throw new Error("Error while processing : graph is disconnected");
		}
		var relatedNto1 = new JoinGraph.GroupSet();
		var related1toN = new JoinGraph.GroupSet();
		for (var __i_enFor2 = 0, __exp_enFor2 = inputGroups._getKeys(), __len_enFor2 = __exp_enFor2.length;
				__i_enFor2 < __len_enFor2; ++__i_enFor2) {
			var g = __exp_enFor2[__i_enFor2];
			if (cardinality == Cardinality.MANY_TO_ONE) {
				this._getRelatedExplore(g, relatedNto1, Cardinality.MANY_TO_ONE);
			} else if (!cardinality) {
				this._getRelatedExplore(g, relatedNto1, Cardinality.MANY_TO_ONE);
				this._getRelatedExplore(g, related1toN, Cardinality.ONE_TO_MANY);
			}
		}
		var allRelatedGroups = new JoinGraph.GroupSet();
		allRelatedGroups._addAll(relatedNto1);
		allRelatedGroups._addAll(related1toN);
		return JoinGraph._getAllQueryItemIDs(allRelatedGroups);
	}

	/**
	 * A set of groups are connected if each group can travel to all the groups in the set.
	 * @param {GroupSet} groups   must be non-empty
	 * @return {boolean}          all groups are connected
	 */
	_isConnected(groups) {
		var connectedSet = this._getRelatedInitial(groups._get(0), null);
		return connectedSet._containsAll(groups);
	}

	/**
	 * Return all connections for a given group based on cardinality. 1:1 cardinality is included if passed 1:N or N:1
	 * @param {Group} group
	 * @param {Cardinality} cardinality
	 * @return {Array}                set of groups related to current group
	 */
	_getConnections(group, cardinality) {
		var adjacentGroups = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = group._getConnectors(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var connector = __exp_enFor0[__i_enFor0];
			if (!cardinality || connector._getCardinality() == cardinality || connector._getCardinality() == Cardinality.ONE_TO_ONE) {
				adjacentGroups.push(this.groupsMap._getByGroupID(connector._getRightGroupID()));
			}
		}
		return adjacentGroups;
	}

	/**
	 * Return all groups related based on given cardinality
	 * @param {Group} initialGroup   group to start traversal
	 * @param {Cardinality} cardinality    enumeration
	 * @return {GroupSet}                set of groups related to current group
	 */
	_getRelatedInitial(initialGroup, cardinality) {
		var relatedGroups = new JoinGraph.GroupSet();
		relatedGroups._add(initialGroup);
		for (var __i_enFor0 = 0, __exp_enFor0 = this._getConnections(initialGroup, cardinality), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var adjacentGroup = __exp_enFor0[__i_enFor0];
			this._getRelatedExplore(adjacentGroup, relatedGroups, cardinality);
		}
		return relatedGroups;
	}

	/**
	 * Add all related groups based on cardinality to the given set
	 * @param {Group} current         current group visiting
	 * @param {GroupSet} relatedGroups   set of groups related to current group
	 * @param {Cardinality} cardinality     cardinality to traverse
	 */
	_getRelatedExplore(current, relatedGroups, cardinality) {
		if (!(relatedGroups._contains(current))) {
			relatedGroups._add(current);
			var connectedGroups = this._getConnections(current, cardinality);
			for (var __i_enFor0 = 0, __len_enFor0 = connectedGroups.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var group = connectedGroups[__i_enFor0];
				this._getRelatedExplore(group, relatedGroups, cardinality);
			}
		}
	}

	/**
	 * Populates groupsMap Creates group object and adds to groupsMap.
	 * @param {Array} joinGraphGroups   group from joingraph endpoint
	 */
	_populateGroups(joinGraphGroups) {
		for (var __i_enFor0 = 0, __exp_enFor0 = joinGraphGroups, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var groupObj = __exp_enFor0[__i_enFor0];
			var groupID = groupObj;
			this.groupsMap._add(new JoinGraph.Group(groupID));
		}
	}

	/**
	 * @param {String} leftGroupID
	 * @param {String} rightGroupID
	 * @param {Cardinality} cardinalityEnum
	 * @param {int} wt
	 * @return {Connector}
	 */
	_createConnector(leftGroupID, rightGroupID, cardinalityEnum, wt) {
		var cnID = this.allConnectors.length;
		var connector = new JoinGraph.Connector(leftGroupID, rightGroupID, cardinalityEnum, cnID, wt);
		this.allConnectors.push(connector);
		return connector;
	}

	/**
	 * Creates connector objects and adds to its groups.
	 * @param {Array} joinGraphConnectors   connector from joingraph endpoint
	 */
	_populateConnectors(joinGraphConnectors) {
		for (var __i_enFor0 = 0, __exp_enFor0 = joinGraphConnectors, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var edge = __exp_enFor0[__i_enFor0];
			var connectorObj = edge;
			var leftGroupID = connectorObj["leftId"];
			var rightGroupID = connectorObj["rightId"];
			var cardinality = connectorObj["cardinality"];
			var w = connectorObj["w"];
			var wt = 17;
			if (w != null) {
				wt = w;
			}
			var cardinalityEnum = JoinGraph._extractCardinality(cardinality);
			if (!cardinalityEnum) {
				continue;
			}
			var connector = this._createConnector(leftGroupID, rightGroupID, cardinalityEnum, wt);
			var oppositeConnector = this._createConnector(rightGroupID, leftGroupID, JoinGraph._getOppositeCardinality(cardinalityEnum), wt);
			if (this.groupsMap._containsGroupID(leftGroupID)) {
				this.groupsMap._getByGroupID(leftGroupID)._addConnector(connector);
			} else {
				var leftGroup = new JoinGraph.Group(leftGroupID);
				leftGroup._addConnector(connector);
				this.groupsMap._add(leftGroup);
			}
			if (this.groupsMap._containsGroupID(rightGroupID)) {
				this.groupsMap._getByGroupID(rightGroupID)._addConnector(oppositeConnector);
			} else {
				var rightGroup = new JoinGraph.Group(rightGroupID);
				rightGroup._addConnector(oppositeConnector);
				this.groupsMap._add(rightGroup);
			}
		}
	}

	/**
	 * Populates queryItemsMap. Creates QueryItem object and adds to its Group
	 * @param {Array} joinGraphQueryItems   queryitems from joingraph end-point
	 */
	_populateQueryItems(joinGraphQueryItems) {
		for (var __i_enFor0 = 0, __exp_enFor0 = joinGraphQueryItems, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var queryItemObj = __exp_enFor0[__i_enFor0];
			var queryItemJSON = queryItemObj;
			var array = Object.keys(queryItemJSON);
			if (array[0] != null) {
				var queryItemID = array[0];
				var groups = queryItemJSON[queryItemID];
				var queryItem = new JoinGraph.QueryItem(queryItemID);
				for (var __i_enFor1 = 0, __exp_enFor1 = groups, __len_enFor1 = __exp_enFor1.length;
						__i_enFor1 < __len_enFor1; ++__i_enFor1) {
					var groupObj = __exp_enFor1[__i_enFor1];
					var groupID = groupObj;
					var group;
					if (this.groupsMap._containsGroupID(groupID)) {
						group = this.groupsMap._getByGroupID(groupID);
					} else {
						group = new JoinGraph.Group(groupID);
					}
					group._addQueryItem(queryItem);
					queryItem._addGroup(group);
				}
				this.queryItemsMap[queryItemID] = queryItem;
			}
		}
	}

	/**
	 * All query item IDs, given a set of related groups
	 * @param {GroupSet} groups          unique set of groups
	 * @return {Array} 				 list of related query items IDs
	 */
	static _getAllQueryItemIDs(groups) {
		var idsMap = {};
		var queryItemIDs = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = groups._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var group = __exp_enFor0[__i_enFor0];
			for (var __i_enFor1 = 0, __exp_enFor1 = group._getQueryItemsIDs(), __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var id = __exp_enFor1[__i_enFor1];
				if (!(idsMap.hasOwnProperty(id))) {
					idsMap[id] = Number.valueOf(1);
					queryItemIDs.push(id);
				}
			}
		}
		return queryItemIDs;
	}

	/**
	 * @param {String} cardinality   String from joingraph
	 * @return {Cardinality}  			   the Cardinality enumeration defined by the string
	 */
	static _extractCardinality(cardinality) {
		if (cardinality == "N:1") {
			return Cardinality.MANY_TO_ONE;
		} else if (cardinality == "1:N") {
			return Cardinality.ONE_TO_MANY;
		} else if (cardinality == "1:1") {
			return Cardinality.ONE_TO_ONE;
		} else if (cardinality == "N:N") {
			return Cardinality.MANY_TO_MANY;
		} else {
			return null;
		}
	}

	/**
	 * @param {Cardinality} cardinality   Cardinality enumeration
	 * @return {Cardinality}  			   The opposite cardinality provided by parameter
	 */
	static _getOppositeCardinality(cardinality) {
		switch (cardinality) {
		case Cardinality.MANY_TO_ONE:
			return Cardinality.ONE_TO_MANY;
		case Cardinality.ONE_TO_MANY:
			return Cardinality.MANY_TO_ONE;
		default:
			return cardinality;
		}
	}

	/**
	 * return group names of a query item.
	 * @param {String} queryItemID  queryItem ID.
	 * @return {Array}  group names.
	 */
	getQueryItemGroupNames(queryItemID) {
		var qiObj = this.queryItemsMap[queryItemID];
		if (!qiObj) {
			return null;
		}
		var grpNames = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = qiObj.groups, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var grp = __exp_enFor0[__i_enFor0];
			grpNames.push(grp.groupID);
		}
		return grpNames;
	}

	/**
	 * @param {Array} queryItemIDs
	 * @param {GroupSet} queryItemGroups
	 * @return {boolean}
	 */
	_getQueryItemsGroups(queryItemIDs, queryItemGroups) {
		for (var __i_enFor0 = 0, __exp_enFor0 = queryItemIDs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var id = __exp_enFor0[__i_enFor0];
			var qiObj = this.queryItemsMap[id];
			if (!qiObj) {
				return false;
			}
			for (var __i_enFor1 = 0, __exp_enFor1 = qiObj._getGroups(), __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var group = __exp_enFor1[__i_enFor1];
				queryItemGroups._add(group);
			}
		}
		return true;
	}

	/**
	 * check if queryItemIDs are related to any in others by 1:N (1:1 is implicit)
	 * @param  queryItemID
	 * @param {Array} others
	 * @return {boolean}  t/f
	 */
	isRelatedOnetoMany(queryItemIDs, others) {
		var queryItemGroups = new JoinGraph.GroupSet();
		if (!(this._getQueryItemsGroups(queryItemIDs, queryItemGroups)) || queryItemGroups._isEmpty()) {
			return false;
		}
		var otherGroups = new JoinGraph.GroupSet();
		if (!(this._getQueryItemsGroups(others, otherGroups)) || otherGroups._isEmpty()) {
			return false;
		}
		var groupsNotInOthers = new JoinGraph.GroupSet();
		for (var __i_enFor0 = 0, __exp_enFor0 = queryItemGroups._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var aGrp = __exp_enFor0[__i_enFor0];
			if (!(otherGroups._contains(aGrp))) {
				groupsNotInOthers._add(aGrp);
			}
		}
		if (groupsNotInOthers._isEmpty()) {
			return true;
		}
		var allGroups = new JoinGraph.GroupSet();
		allGroups._addAll(groupsNotInOthers);
		allGroups._addAll(otherGroups);
		if (!(this._isConnected(allGroups))) {
			return false;
		}
		for (var __i_enFor1 = 0, __exp_enFor1 = groupsNotInOthers._getKeys(), __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var grp = __exp_enFor1[__i_enFor1];
			var related = this._getRelatedInitial(grp, Cardinality.ONE_TO_MANY);
			if (!(related._containsAny(otherGroups))) {
				return false;
			}
		}
		return true;
	}

	/**
	 * @param {Array} unwanted
	 */
	setUnwantedCardinality(unwanted) {
		this.unwantedCardinality = unwanted;
	}

	/**
	 * Dijkstra
	 * @param {String} srcGrpID
	 * @param {Array} grpIDs
	 * @return {Array}
	 */
	calculateShortestPathFromSource(srcGrpID, grpIDs) {
		var i = 0;
		for (i = 0; i < this.groupsMap._size(); ++i) {
			var aGrp = this.groupsMap._get(i);
			aGrp._setDistance(2147483647);
			aGrp._setPrevConnector(null);
		}
		var srcGrp = this.groupsMap._getByGroupID(srcGrpID);
		var targetGrps = new JoinGraph.GroupSet();
		for (var __i_enFor0 = 0, __exp_enFor0 = grpIDs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var aG = __exp_enFor0[__i_enFor0];
			targetGrps._add(this.groupsMap._getByGroupID(aG));
		}
		var targetVisited = new JoinGraph.GroupSet();
		var settledGrps = new JoinGraph.GroupSet();
		var unsettledGrps = new JoinGraph.GroupSet();
		srcGrp._setDistance(0);
		unsettledGrps._add(srcGrp);
		var currentGroup = JoinGraph._getLowestDistanceGroup(unsettledGrps, settledGrps);
		while (currentGroup) {
			settledGrps._add(currentGroup);
			var connectorsForNeighbours = this._getConnectorsForUnvisitedNeighbours(currentGroup, settledGrps);
			for (var __i_enFor1 = 0, __exp_enFor1 = connectorsForNeighbours, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var c = __exp_enFor1[__i_enFor1];
				var adjacentGrpID = c._getRightGroupID();
				var adjacentGrp = this.groupsMap._getByGroupID(adjacentGrpID);
				JoinGraph._calculateMinimumDistance(adjacentGrp, c, currentGroup);
				unsettledGrps._add(adjacentGrp);
			}
			if (targetGrps._contains(currentGroup)) {
				targetVisited._add(currentGroup);
				if (targetGrps._size() == targetVisited._size()) {
					break;
				}
			}
			currentGroup = JoinGraph._getLowestDistanceGroup(unsettledGrps, settledGrps);
		}
		var rt = [];
		var targetSeen = new JoinGraph.GroupSet();
		var aGrp = JoinGraph._getLowestDistanceGroup(targetVisited, targetSeen);
		while (aGrp) {
			targetSeen._add(aGrp);
			rt.push(aGrp._getGroupID());
			aGrp = JoinGraph._getLowestDistanceGroup(targetVisited, targetSeen);
		}
		return rt;
	}

	/**
	 * get group with lowest distance.
	 * @param {GroupSet} unsettledGrps
	 * @param {GroupSet} settledGrps
	 * @return {Group}
	 */
	static _getLowestDistanceGroup(unsettledGrps, settledGrps) {
		var lowestDistanceGroup;
		var lowestDistance = 2147483647;
		for (var i = 0; i < unsettledGrps._size(); ++i) {
			var grp = unsettledGrps._get(i);
			if (settledGrps._contains(grp)) {
				continue;
			}
			var groupDistance = grp._getDistance();
			if (groupDistance < lowestDistance) {
				lowestDistance = groupDistance;
				lowestDistanceGroup = grp;
			}
		}
		return lowestDistanceGroup;
	}

	/**
	 * 1-N / N-N is also dropped because it is NOT allowed from a fact group.
	 * @param {Group} currentGroup
	 * @param {GroupSet} settledGrps
	 * @return {Array}
	 */
	_getConnectorsForUnvisitedNeighbours(currentGroup, settledGrps) {
		var rt = [];
		var connectors = currentGroup._getConnectors();
		for (var __i_enFor0 = 0, __exp_enFor0 = connectors, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var c = __exp_enFor0[__i_enFor0];
			if (this._isUnwantedCardinality(c._getCardinality()) || settledGrps._containsGroupID(c._getRightGroupID())) {
				continue;
			}
			rt.push(c);
		}
		return rt;
	}

	/**
	 * @param {Cardinality} card
	 * @return {boolean}
	 */
	_isUnwantedCardinality(card) {
		return this.unwantedCardinality && this.unwantedCardinality.indexOf(card)> -1;
	}

	/**
	 * @param {Group} evaluationGroup
	 * @param {Connector} connector
	 * @param {Group} sourceGroupe
	 */
	static _calculateMinimumDistance(evaluationGroup, connector, sourceGroupe) {
		var sourceDistance = sourceGroupe._getDistance();
		if (sourceDistance + connector._getWeight() < evaluationGroup._getDistance()) {
			evaluationGroup._setDistance(sourceDistance + connector._getWeight());
			evaluationGroup._setPrevConnector(connector);
		}
	}

	/**
	 * @param {String} srcGrpID
	 * @param {String} targetGrpID
	 * @return {Array}
	 */
	getShortestPathFromSource(srcGrpID, targetGrpID) {
		var rt = [];
		var targetGrp = this.groupsMap._getByGroupID(targetGrpID);
		while (targetGrp) {
			var cn = targetGrp._getPrevConnector();
			var cnID = cn._getConnectorID();
			if (cnID >= 1000000) {
				cnID = cnID - 1000000;
			}
			rt.push(cnID);
			if (cn._getLeftGroupID() == srcGrpID) {
				break;
			}
			targetGrp = this.groupsMap._getByGroupID(cn._getLeftGroupID());
		}
		return rt;
	}

	/**
	 * @param {Array} connectorIds
	 * @param {Array} prefer
	 * @return {JoinGraph}
	 */
	createMergedJoinGraph(connectorIds, prefer) {
		var newJG = new JoinGraph(null);
		newJG.unwantedCardinality = this.unwantedCardinality;
		var dummyStart = new JoinGraph.Group("theDummyGroupID");
		newJG.groupsMap._add(dummyStart);
		var srcGrps = new JoinGraph.GroupSet();
		var i = 0;
		for (i = 0; i < connectorIds.length; ++i) {
			var pos = connectorIds[i];
			var cn = this.allConnectors[pos];
			srcGrps._add(this.groupsMap._getByGroupID(cn.leftGroupID));
			srcGrps._add(this.groupsMap._getByGroupID(cn.rightGroupID));
		}
		for (i = 0; i < this.groupsMap._size(); ++i) {
			var aGrp = this.groupsMap._get(i);
			if (srcGrps._containsGroupID(aGrp._getGroupID())) {
				continue;
			}
			this._tryToAddDummyConnectorIfAdjacent(newJG, aGrp, srcGrps, dummyStart, prefer);
		}
		for (i = 0; i < this.allConnectors.length; ++i) {
			var aC = this.allConnectors[i];
			var leftGrpId = aC._getLeftGroupID();
			var rightGrpId = aC._getRightGroupID();
			if (srcGrps._containsGroupID(leftGrpId) || srcGrps._containsGroupID(rightGrpId)) {
				continue;
			}
			var newConnector = new JoinGraph.Connector(leftGrpId, rightGrpId, aC._getCardinality(), aC._getConnectorID(), aC._getWeight());
			if (newJG.groupsMap._containsGroupID(leftGrpId)) {
				newJG.groupsMap._getByGroupID(leftGrpId)._addConnector(newConnector);
			} else {
				var newLeftGroup = new JoinGraph.Group(leftGrpId);
				newLeftGroup._addConnector(newConnector);
				newJG.groupsMap._add(newLeftGroup);
			}
		}
		return newJG;
	}

	/**
	 * @param {JoinGraph} newJG
	 * @param {Group} aGrp
	 * @param {GroupSet} srcGrps
	 * @param {Group} dummyStart
	 * @param {Array} prefer
	 */
	_tryToAddDummyConnectorIfAdjacent(newJG, aGrp, srcGrps, dummyStart, prefer) {
		var possibleConnectors = [];
		var i = 0;
		for (i = 0; i < srcGrps._size(); ++i) {
			var aSrcGrp = srcGrps._get(i);
			for (var __i_enFor0 = 0, __exp_enFor0 = aSrcGrp.connectors, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var c = __exp_enFor0[__i_enFor0];
				var rightGrpID = c._getRightGroupID();
				if (!(rightGrpID == aGrp._getGroupID()) || this._isUnwantedCardinality(c._getCardinality())) {
					continue;
				}
				possibleConnectors.push(c);
			}
		}
		if (possibleConnectors.length==0) {
			return;
		}
		var w = 2147483647;
		var lowestConnector;
		for (var __i_enFor1 = 0, __exp_enFor1 = possibleConnectors, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var aC = __exp_enFor1[__i_enFor1];
			if (JoinGraph._needUpdateLowestConnector(aC, w, lowestConnector, prefer)) {
				w = aC._getWeight();
				lowestConnector = aC;
			}
		}
		var dummyConnector = new JoinGraph.Connector("theDummyGroupID", aGrp._getGroupID(), lowestConnector._getCardinality(), lowestConnector._getConnectorID() + 1000000, lowestConnector._getWeight());
		dummyStart._addConnector(dummyConnector);
		var newAdjacentGrp = new JoinGraph.Group(aGrp._getGroupID());
		newJG.groupsMap._add(newAdjacentGrp);
	}

	/**
	 * @param {Connector} aC
	 * @param {int} w
	 * @param {Connector} lowestConnector
	 * @param {Array} prefer
	 * @return {boolean}
	 */
	static _needUpdateLowestConnector(aC, w, lowestConnector, prefer) {
		if (aC._getWeight() < w) {
			return true;
		}
		if (aC._getWeight() > w) {
			return false;
		}
		return lowestConnector._getCardinality() != Cardinality.ONE_TO_ONE && aC._getCardinality() == Cardinality.ONE_TO_ONE || !(prefer.indexOf(lowestConnector._getLeftGroupID())> -1) && prefer.indexOf(aC._getLeftGroupID())> -1;
	}

	/**
	 * @param {String} leftGrp
	 * @param {Array} connectorIds
	 * @param {Array} rightIds
	 * @param {Array} cards
	 */
	getRightGroup(leftGrp, connectorIds, rightIds, cards) {
		for (var i = 0; i < connectorIds.length; ++i) {
			var pos = connectorIds[i];
			var c = this.allConnectors[pos];
			if (leftGrp == c._getLeftGroupID()) {
				rightIds.push(c._getRightGroupID());
				cards.push(c._getCardinality());
			}
		}
	}
}
JoinGraph.Connector = class extends BaseMoserObject{

	leftGroupID = null;

	rightGroupID = null;

	cardinality = null;

	connectorID = 0;

	weight = 0;



	constructor(leftGroupID, rightGroupID, cardinality, id, w) {
		super();
		this.leftGroupID = leftGroupID;
		this.rightGroupID = rightGroupID;
		this.cardinality = cardinality;
		this.connectorID = id;
		this.weight = w;
	}

	_getCardinality() {
		return this.cardinality;
	}

	_getLeftGroupID() {
		return this.leftGroupID;
	}

	_getRightGroupID() {
		return this.rightGroupID;
	}

	_getConnectorID() {
		return this.connectorID;
	}

	_getWeight() {
		return this.weight;
	}
}
JoinGraph.GroupSet = class extends BaseMoserObject{

	map = null;

	keys = null;



	constructor() {
		super();
		this.map = {};
		this.keys = [];
	}

	_add(item) {
		if (!(this.map.hasOwnProperty(item._getGroupID()))) {
			this.map[item._getGroupID()] = item;
			this.keys.push(item);
		}
	}

	_addAll(items) {
		for (var __i_enFor0 = 0, __exp_enFor0 = items._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			this._add(item);
		}
	}

	_addAllList(items) {
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			this._add(item);
		}
	}

	_contains(item) {
		return this.map.hasOwnProperty(item._getGroupID());
	}

	_clear() {
		for (var __i_enFor0 = 0, __exp_enFor0 = this._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var group = __exp_enFor0[__i_enFor0];
			delete this.map[group._getGroupID()];
		}
		this.keys.length = 0;
	}

	_containsAll(groups) {
		for (var __i_enFor0 = 0, __exp_enFor0 = groups._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var group = __exp_enFor0[__i_enFor0];
			if (!(this._contains(group))) {
				return false;
			}
		}
		return true;
	}

	_intersect(other) {
		var output = new JoinGraph.GroupSet();
		for (var __i_enFor0 = 0, __exp_enFor0 = other._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var group = __exp_enFor0[__i_enFor0];
			if (this._contains(group)) {
				output._add(group);
			}
		}
		return output;
	}

	_containsAllList(groups) {
		for (var __i_enFor0 = 0, __exp_enFor0 = groups, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var group = __exp_enFor0[__i_enFor0];
			if (!(this._contains(group))) {
				return false;
			}
		}
		return true;
	}

	_containsAny(groups) {
		for (var __i_enFor0 = 0, __exp_enFor0 = groups._getKeys(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var group = __exp_enFor0[__i_enFor0];
			if (this._contains(group)) {
				return true;
			}
		}
		return false;
	}

	_get(i) {
		return this.keys[i];
	}

	_getKeys() {
		return this.keys;
	}

	_size() {
		return this.keys.length;
	}

	_isEmpty() {
		return this.keys.length==0;
	}

	_containsGroupID(groupID) {
		return this.map.hasOwnProperty(groupID);
	}

	_getByGroupID(groupID) {
		return this.map[groupID];
	}

	toString() {
		return this._getKeys().toString();
	}
}
JoinGraph.Group = class extends BaseMoserObject{

	groupID = null;

	queryItems = null;

	connectors = null;

	distance = 0;

	prevConnector = null;



	constructor(groupID) {
		super();
		this.groupID = groupID;
		this.queryItems = [];
		this.connectors = [];
	}

	_getQueryItemsIDs() {
		var queryItemIDs = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = this._getQueryItems(), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var queryItem = __exp_enFor0[__i_enFor0];
			queryItemIDs.push(queryItem._getQueryItemID());
		}
		return queryItemIDs;
	}

	_addConnector(connector) {
		this.connectors.push(connector);
	}

	_addQueryItem(queryItem) {
		this.queryItems.push(queryItem);
	}

	_getGroupID() {
		return this.groupID;
	}

	_getQueryItems() {
		return this.queryItems;
	}

	_getConnectors() {
		return this.connectors;
	}

	_getDistance() {
		return this.distance;
	}

	_setDistance(w) {
		this.distance = w;
	}

	_getPrevConnector() {
		return this.prevConnector;
	}

	_setPrevConnector(c) {
		this.prevConnector = c;
	}

	toString() {
		return this._getGroupID();
	}
}
JoinGraph.QueryItem = class extends BaseMoserObject{

	queryItemID = null;

	groups = null;



	constructor(queryItemID) {
		super();
		this.queryItemID = queryItemID;
		this.groups = [];
	}

	_getQueryItemID() {
		return this.queryItemID;
	}

	_getGroups() {
		return this.groups;
	}

	_addGroup(group) {
		this.groups.push(group);
	}
}


// WEBPACK FOOTER //
// ./src/generated/JoinGraph.js